import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Time to consider data stale (5 minutes)
      staleTime: 5 * 60 * 1000,
      // Time to keep data in cache (10 minutes)
      gcTime: 10 * 60 * 1000,
      // Refetch on window focus
      refetchOnWindowFocus: false,
      // Retry failed requests
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      // Retry delay with exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
      // Don't retry mutations by default (they might have side effects)
      retryDelay: 1000,
    },
  },
});

// Query keys factory for consistent naming
export const queryKeys = {
  // Health check
  health: () => ['health'] as const,

  // Users
  users: {
    all: () => ['users'] as const,
    lists: () => [...queryKeys.users.all(), 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.users.lists(), { filters }] as const,
    details: () => [...queryKeys.users.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
  },

  // Employees
  employees: {
    all: () => ['employees'] as const,
    lists: () => [...queryKeys.employees.all(), 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.employees.lists(), { filters }] as const,
    details: () => [...queryKeys.employees.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.employees.details(), id] as const,
  },

  // Departments
  departments: {
    all: () => ['departments'] as const,
    lists: () => [...queryKeys.departments.all(), 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.departments.lists(), { filters }] as const,
    details: () => [...queryKeys.departments.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.departments.details(), id] as const,
  },

  // Locations
  locations: {
    all: () => ['locations'] as const,
    lists: () => [...queryKeys.locations.all(), 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.locations.lists(), { filters }] as const,
    details: () => [...queryKeys.locations.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.locations.details(), id] as const,
  },

  // Schedules
  schedules: {
    all: () => ['schedules'] as const,
    lists: () => [...queryKeys.schedules.all(), 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.schedules.lists(), { filters }] as const,
    details: () => [...queryKeys.schedules.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.schedules.details(), id] as const,
  },

  // Attendance
  attendance: {
    all: () => ['attendance'] as const,
    events: () => [...queryKeys.attendance.all(), 'events'] as const,
    event: (filters: Record<string, any>) => [...queryKeys.attendance.events(), { filters }] as const,
  },

  // Time Records
  timeRecords: {
    all: () => ['time-records'] as const,
    lists: () => [...queryKeys.timeRecords.all(), 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.timeRecords.lists(), { filters }] as const,
    aggregates: () => [...queryKeys.timeRecords.all(), 'aggregates'] as const,
    aggregate: (filters: Record<string, any>) => [...queryKeys.timeRecords.aggregates(), { filters }] as const,
  },

  // Organizations
  organizations: {
    all: () => ['organizations'] as const,
    lists: () => [...queryKeys.organizations.all(), 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.organizations.lists(), { filters }] as const,
    details: () => [...queryKeys.organizations.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.organizations.details(), id] as const,
    stats: (id: string) => [...queryKeys.organizations.all(), 'stats', id] as const,
    withStats: (id: string) => [...queryKeys.organizations.all(), 'with-stats', id] as const,
    employees: (id: string, filters?: Record<string, any>) => [...queryKeys.organizations.all(), 'employees', id, { filters }] as const,
    headquarters: () => [...queryKeys.organizations.all(), 'headquarters'] as const,
  },

  // Positions
  positions: {
    all: () => ['positions'] as const,
    lists: () => [...queryKeys.positions.all(), 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.positions.lists(), { filters }] as const,
    details: () => [...queryKeys.positions.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.positions.details(), id] as const,
  },
} as const;